import type { ReactNode } from 'react';
import { Link } from 'react-router';

export interface KpiProps {
  label: string;
  value: string;
  /** Delta text; hidden when empty (`.delta:empty`). */
  delta?: string;
  /** `delta--up`, `delta--down`, `delta--flat` or `delta--warn`. */
  tone?: string;
  sub?: ReactNode;
  /** Caveats under the meta line, one line each (for example the MRR on unpaid invoices). */
  notes?: string[];
  to?: string;
  /** Help bulb in the top-right corner (outside the link, so it does not navigate). */
  help?: ReactNode;
}

/**
 * `.kpi` tile; an anchor when `to` is set. A delta that rounds to zero ("±0", "±0.0%", "±0.0 pp")
 * is always shown neutral, whatever tone the caller computed from the unrounded change.
 */
export function Kpi({ label, value, delta = '', tone, sub, notes, to, help }: KpiProps) {
  const toneClass = delta.startsWith('±') && tone !== 'delta--warn' ? 'delta--flat' : tone;
  const body = (
    <>
      <span className="kpi__label">{label}</span>
      <span className="kpi__value">{value}</span>
      <span className="kpi__meta">
        <span className={toneClass ? `delta ${toneClass}` : 'delta'}>{delta}</span>
        <span>{sub}</span>
        {notes?.map((note) => (
          <span className="kpi__note" key={note}>
            {note}
          </span>
        ))}
      </span>
    </>
  );
  const tile = to ? (
    <Link className="kpi" to={to}>
      {body}
    </Link>
  ) : (
    <div className="kpi">{body}</div>
  );
  if (!help) return tile;
  return (
    <div className="kpi-wrap">
      {tile}
      <span className="kpi__help">{help}</span>
    </div>
  );
}

export function KpiGrid({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="kpi-grid" aria-label={label}>
      {children}
    </section>
  );
}
